import React, { useState } from 'react';
import { Table, Button, Tag, Space, Input, Typography, Spin, Empty } from 'antd';
import { SearchOutlined, StarOutlined, UserOutlined, CalendarOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import MentorRatingModal from '../../../components/Common/MentorRatingModal';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Search } = Input;

interface RatingApplication {
  id: number;
  name: string;
  type: number;
  mentorId: number;
  menteeId: number;
  eventDate?: string;
}

const MentorRatingTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<RatingApplication | null>(null);

  const { data: applications = [], isLoading: loading } = useQuery({
    queryKey: menteeProfileQueryKeys.mentorRatingApplications,
    queryFn: async () => {
      const res = await apiService.getMenteeMentorRatingApplications();
      if (!res.success) throw new Error(res.message);
      return res.data ?? [];
    },
  });

  const getTypeText = (type: number) => (type === 0 ? '1-1 Görüşme' : 'Bootcamp');

  const handleRate = (application: RatingApplication) => {
    setSelectedApplication(application);
    setModalVisible(true);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.mentorRatingApplications });
  };

  const filteredApplications = applications.filter(
    (app: RatingApplication) =>
      app.name.toLowerCase().includes(searchText.toLowerCase()) ||
      getTypeText(app.type).toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Mentör / Etkinlik',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: RatingApplication) => (
        <Space>
          {record.type === 0 ? <UserOutlined /> : <CalendarOutlined />}
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Tip',
      dataIndex: 'type',
      key: 'type',
      render: (type: number) => (
        <Tag color={type === 0 ? 'blue' : 'green'}>{getTypeText(type)}</Tag>
      ),
    },
    {
      title: 'Tarih',
      dataIndex: 'eventDate',
      key: 'eventDate',
      render: (eventDate?: string) => (
        <Text type="secondary">{eventDate ? dayjs(eventDate).format('DD MMM YYYY HH:mm') : '-'}</Text>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: RatingApplication) => (
        <Button type="primary" icon={<StarOutlined />} size="small" onClick={() => handleRate(record)}>
          Mentörü Değerlendir
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <PanelTabCard
        title="Mentör Değerlendirme"
        description="Tamamlanan görüşme ve bootcamp'ler için mentörünüzü puanlayabilirsiniz."
      >
        <Search
          placeholder="Mentör veya etkinlik ara..."
          allowClear
          onSearch={setSearchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: 16, maxWidth: 360 }}
          prefix={<SearchOutlined />}
        />

        {filteredApplications.length === 0 ? (
          <Empty description="Değerlendirilebilecek mentör bulunamadı" />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredApplications}
            rowKey={(record) => `${record.type}-${record.id}`}
            pagination={{ pageSize: 10 }}
          />
        )}
      </PanelTabCard>

      <MentorRatingModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedApplication(null);
        }}
        onSuccess={handleModalSuccess}
        application={selectedApplication}
      />
    </>
  );
};

export default MentorRatingTab;
